/**
 * cloudAlertsApi.js — API functions for the V2 Cloud Alerts page (5.0 platform).
 *
 * fetchCloudMaintenance — polls device maintenance (batteries, comm lost, other)
 *                          via `getdevicemaintenance` stored procedure (60s interval)
 *
 * acceptAlert  — POST /emergency/accept  (write accept datetime + user info to the alarm)
 * finishAlert  — POST /emergency/finish  (write finish datetime + reason to the alarm)
 *
 * Both emergency endpoints expect a `{ "payload": {...} }` wrapper and return
 * `{ Status: "OK" }` on success. On backend update the AppSync subscription
 * pokes all connected clients so the row repaints naturally — no optimistic
 * client mutation needed.
 */

import { apiFetch } from '../../../../services/api.js';
import { getAuthHeaders } from '../../../../services/auth.js';

// ---------------------------------------------------------------------------
// Base URL helper (mirrors src/services/api.js pattern)
// ---------------------------------------------------------------------------
const getBaseUrl = () =>
    import.meta.env.VITE_DEVMODE === 'true'
        ? import.meta.env.VITE_DEVAPI
        : import.meta.env.VITE_API;

/**
 * Low-level POST to an /emergency/* endpoint.
 * @param {string} path — route suffix, e.g. "/accept" or "/finish"
 * @param {object} payload — inner payload object (wrapped in `{payload: ...}` by this helper)
 * @returns {Promise<[boolean, string?]>}
 */
async function postEmergency(path, payload) {
    try {
        const url = getBaseUrl() + import.meta.env.VITE_EVENT_E_ENDPOINT + path;
        const resp = await fetch(url, {
            method: 'POST',
            headers: await getAuthHeaders(),
            body: JSON.stringify({ payload }),
        });

        // 500/503/403 won't necessarily parse as JSON — handle them first.
        if (resp.status === 403) return [false, 'Forbidden, user not authorized.'];
        if (resp.status === 500) return [false, 'Internal server error. Please contact support.'];
        if (resp.status === 503) return [false, 'Service unavailable. Please try again.'];

        let data = null;
        try { data = await resp.json(); } catch { /* swallow — treat as unknown */ }

        if (data?.Status === 'OK') return [true];

        const code = data?.ErrorCode ? ` (${data.ErrorCode})` : '';
        return [false, `Request failed${code}.`];
    } catch (err) {
        console.error(`[cloudAlertsApi] postEmergency(${path}) error:`, err);
        return [false, 'Request failed. Please check your connection and try again.'];
    }
}

// ---------------------------------------------------------------------------
// Fetch Cloud Maintenance (batteries + communication lost + other)
// ---------------------------------------------------------------------------

/**
 * Calls `getdevicemaintenance` stored procedure.
 * Returns flat array of maintenance items with snake_case fields:
 *   { event_datetime_utc, condition, device_name, manufacture_uuid,
 *     manufacture_id, device_id, area_name, area_id, resident_name, resident_id }
 *
 * The caller splits by `condition` into batteries / lost / other buckets.
 *
 * @param {number} campusId — numeric campus ID
 * @returns {Promise<Array>}
 */
export function fetchCloudMaintenance(campusId) {
    return apiFetch({
        procedure: 'getdevicemaintenance',
        reqType: 'stored',
        pKey: 'campus_id',
        pVal: campusId,
        readOnly: true,
    });
}

// ---------------------------------------------------------------------------
// Accept an emergency alert
// ---------------------------------------------------------------------------

/**
 * Accept an alert — writes accepted_datetime_utc / accepted_user_id /
 * accepted_username / accepted_display onto the DynamoDB alarm row.
 *
 * Backend validator requires: campus_id, event_id, user_id, display_name, username.
 *
 * @param {object} args
 * @param {number} args.campusId
 * @param {string} args.eventId
 * @param {number} args.userId
 * @param {string} args.username
 * @param {string} args.displayName
 * @returns {Promise<[boolean, string?]>}
 */
export function acceptAlert({ campusId, eventId, userId, username, displayName }) {
    return postEmergency('/accept', {
        campus_id: campusId,
        event_id: eventId,
        user_id: userId,
        username: username || '',
        display_name: displayName || username || '',
    });
}

// ---------------------------------------------------------------------------
// Finish an emergency alert
// ---------------------------------------------------------------------------

/**
 * Finish an alert — writes finish_datetime_utc, reason_id, resident_id onto
 * the DynamoDB alarm row.
 *
 * Backend validator requires: campus_id, event_id, reason_id.
 * resident_id is optional on the backend (defaults to 0) but we pass it
 * through when known so the archived row is complete.
 *
 * @param {object} args
 * @param {number} args.campusId
 * @param {string} args.eventId
 * @param {number} args.reasonId — cloud_reason_code_id from core_reason_codes
 * @param {number} [args.residentId]
 * @returns {Promise<[boolean, string?]>}
 */
export function finishAlert({ campusId, eventId, reasonId, residentId }) {
    return postEmergency('/finish', {
        campus_id: campusId,
        event_id: eventId,
        reason_id: reasonId,
        resident_id: residentId || 0,
    });
}
